<template>
  <div>
    <FormGroup>
      <Text variant="h2">Property</Text>

      <FormField label="Property type" :error="form.fieldErrors('propertyType')">
        <Select v-model="form.fields.propertyType">
          <option v-for="option in propertyTypeOptions" :key="option" :value="option">{{ option }}</option>
        </Select>
      </FormField>

      <FormField
        v-if="isApartmentNumberFieldRequired"
        label="Apartment number"
        :error="form.fieldErrors('apartmentNumber')"
      >
        <Input v-model="form.fields.apartmentNumber" placeholder="What is the apartment number?">
          <template #prefix>#</template>
        </Input>
      </FormField>

      <FormField label="Architectural style" :error="form.fieldErrors('architectureStyle')">
        <Select v-model="form.fields.architectureStyle">
          <option v-for="option in architectureStyleOptions" :key="option" :value="option">{{ option }}</option>
        </Select>
      </FormField>

      <FormField label="Year built" :error="form.fieldErrors('yearBuilt')">
        <Input v-model="form.fields.yearBuilt" type="number" placeholder="What year was the property built?" />
      </FormField>

      <FormField label="Total number of floors" :error="form.fieldErrors('numberOfFloors')">
        <Input v-model="form.fields.numberOfFloors" type="number" min="0" name="numberOfFloors" />
      </FormField>

      <FormField label="Which floor is the property on?" :error="form.fieldErrors('apartmentFloor')">
        <Input
          v-model="form.fields.apartmentFloor"
          type="number"
          min="0"
          :max="form.fields.numberOfFloors"
          name="apartmentFloor"
        />
      </FormField>

      <Text variant="h2">Interior</Text>

      <FormField label="Property Size" :error="form.fieldErrors('squareFeet')">
        <Input
          v-model="form.fields.squareFeet"
          type="number"
          min="0"
          name="squareFeet"
          placeholder="Enter a whole number, e.g. 850"
          @keydown="forbidCharacters"
        >
          <template #suffix>ft²</template>
        </Input>
      </FormField>

      <FormField label="Bedrooms" direction="row" :error="form.fieldErrors('bedrooms')">
        <InputStepper v-model="form.fields.bedrooms" name="bedrooms" />
      </FormField>

      <FormField label="Full bathrooms" direction="row" :error="form.fieldErrors('fullBathrooms')">
        <InputStepper v-model="form.fields.fullBathrooms" name="fullBathrooms" />
      </FormField>

      <FormField label="Half bathrooms" direction="row" :error="form.fieldErrors('halfBathrooms')">
        <InputStepper v-model="form.fields.halfBathrooms" name="halfBathrooms" />
      </FormField>
    </FormGroup>
  </div>
</template>

<script setup lang="ts">
import { requiredIf } from '@vuelidate/validators';
import { computed } from 'vue';

import { FormField, FormGroup, Select, Input, InputStepper, useForm, Text } from '@tallo/design-system';
import { PropertyType, ArchitectureStyle } from '@tallo/property';

import { SaveLocationDto } from '../../dtos/save-location.dto.interface';
import { SavePropertySpecificationsDto } from '../../dtos/save-property-specifications.dto.interface';
import { usePropertyStore } from '../../stores/property.store';
import { isApartmentNumberRequired } from '../../utils/apartment-number-required';

const propertyTypeOptions = Object.values(PropertyType);
const architectureStyleOptions = Object.values(ArchitectureStyle);
const propertyStore = usePropertyStore();
const isApartmentNumberFieldRequired = computed(() => isApartmentNumberRequired(form.fields.propertyType));
const form = useForm<SavePropertySpecificationsDto & Pick<SaveLocationDto, 'apartmentNumber'>>(
  {
    apartmentNumber: propertyStore.property?.location?.apartmentNumber || null,
    propertyType: propertyStore.property?.specifications?.propertyType || null,
    architectureStyle: propertyStore.property?.specifications?.architectureStyle || null,
    yearBuilt: propertyStore.property?.specifications?.yearBuilt ?? null,
    apartmentFloor: propertyStore.property?.specifications?.apartmentFloor ?? 1,
    numberOfFloors: propertyStore.property?.specifications?.numberOfFloors ?? 1,
    squareFeet: propertyStore.property?.specifications?.squareFeet ?? null,
    bedrooms: propertyStore.property?.specifications?.bedrooms || 0,
    fullBathrooms: propertyStore.property?.specifications?.fullBathrooms || 0,
    halfBathrooms: propertyStore.property?.specifications?.halfBathrooms || 0,
  },
  {
    apartmentNumber: {
      required: requiredIf((): boolean => isApartmentNumberFieldRequired.value),
    },
  },
);

async function save() {
  return await form.submit((fields) => {
    const apartmentNumber = isApartmentNumberFieldRequired.value ? fields.apartmentNumber : null;

    return Promise.all([propertyStore.saveSpecifications(fields), propertyStore.saveLocation({ apartmentNumber })]);
  });
}

const forbidCharacters = (event: KeyboardEvent) => {
  if (event.key === '.' || event.key === ',' || event.key.toLowerCase() === 'e') {
    event.preventDefault();
  }
};

defineExpose({ save, form });
</script>
